import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined, TeamOutlined } from "@ant-design/icons";
import { Button, Card, Form, Input, message, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { api } from "../services/apiClient";
import { canUseUserFilter, fetchUserOptions, userQuery } from "../utils/adminUserFilter";

const { Title, Text } = Typography;

function labelOf(item, fields) {
  return fields.map((field) => item?.[field]).find(Boolean) || item?.id;
}

export default function AutoUploadTargets() {
  const [search, setSearch] = useState("");
  const [groups, setGroups] = useState([]);
  const [channels, setChannels] = useState([]);
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [facebookPages, setFacebookPages] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const showUserFilter = canUseUserFilter();

  const load = useCallback(async () => {
    try {
      const [groupItems, channelItems, tiktokItems, facebookItems] = await Promise.all([
        api.get(`/api/upload-groups${userQuery(selectedUserId)}`),
        api.get(`/api/channels${userQuery(selectedUserId)}`),
        api.get(`/api/tiktok-accounts${userQuery(selectedUserId)}`),
        api.get(`/api/facebook-pages${userQuery(selectedUserId)}`),
      ]);
      setGroups(groupItems || []);
      setChannels(channelItems || []);
      setTiktokAccounts(tiktokItems || []);
      setFacebookPages(facebookItems || []);
    } catch (error) {
      message.error(error.message);
    }
  }, [selectedUserId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!showUserFilter) return;
    fetchUserOptions().then(setUsers).catch((error) => message.error(error.message));
  }, [showUserFilter]);

  const data = useMemo(() => {
    const keyword = search.toLowerCase();
    return groups.filter((item) => `${item.name || ""} ${item.description || ""}`.toLowerCase().includes(keyword));
  }, [groups, search]);

  const openModal = (item = null) => {
    setEditing(item);
    form.setFieldsValue(item || {});
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      await api.post("/api/upload-groups", { ...editing, ...values });
      message.success("Đã lưu nhóm upload");
      closeModal();
      load();
    } catch (error) {
      if (error.message) message.error(error.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/upload-groups/${id}`);
      message.success("Đã xóa nhóm upload");
      load();
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    { title: "NHÓM UPLOAD", render: (_, item) => <div><Text strong>{item.name}</Text><br /><Text type="secondary">{item.description || "—"}</Text></div> },
    { title: "YOUTUBE", render: (_, item) => <Tag color="red">{item.youtubeChannelIds?.length || 0} kênh</Tag> },
    { title: "TIKTOK", render: (_, item) => <Tag color="purple">{item.tiktokAccountIds?.length || 0} account</Tag> },
    { title: "FACEBOOK", render: (_, item) => <Tag color="blue">{item.facebookPageIds?.length || 0} page</Tag> },
    {
      title: "",
      render: (_, item) => (
        <Space className="provider-actions">
          <Tooltip title="Sửa nhóm">
            <Button aria-label="Sửa nhóm" className="provider-action-button" icon={<EditOutlined />} onClick={() => openModal(item)} />
          </Tooltip>
          <Popconfirm title="Xóa nhóm này?" onConfirm={() => remove(item.id)}>
            <Tooltip title="Xóa nhóm">
              <Button aria-label="Xóa nhóm" danger className="provider-action-button" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-heading">
        <div>
          <Title level={3}>Nhóm Upload</Title>
          <Text type="secondary">Gom nhiều kênh và nhiều nền tảng thành một nhóm upload.</Text>
        </div>
      </div>
      <Card className="criclebox">
        <div className="auto-toolbar">
          <Input className="auto-toolbar-search" allowClear prefix={<SearchOutlined />} placeholder="Tìm nhóm upload" value={search} onChange={(event) => setSearch(event.target.value)} />
          {showUserFilter ? (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Tìm theo người dùng"
              style={{ minWidth: 240 }}
              value={selectedUserId}
              onChange={setSelectedUserId}
              options={users}
            />
          ) : null}
          <Button className="auto-toolbar-primary" type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Thêm nhóm
          </Button>
        </div>
        <Table rowKey="id" columns={columns} dataSource={data} />
      </Card>

      <Modal title={editing ? "Sửa nhóm upload" : "Thêm nhóm upload"} visible={open} onCancel={closeModal} onOk={save} okText="Lưu">
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Tên nhóm" rules={[{ required: true, message: "Nhập tên nhóm" }]}>
            <Input prefix={<TeamOutlined />} />
          </Form.Item>
          <Form.Item name="description" label="Mô tả"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="youtubeChannelIds" label="YouTube Channels">
            <Select mode="multiple" options={channels.map((item) => ({ value: item.id, label: labelOf(item, ["channelTitle", "channelName", "channelId"]) }))} />
          </Form.Item>
          <Form.Item name="tiktokAccountIds" label="TikTok Accounts">
            <Select mode="multiple" options={tiktokAccounts.map((item) => ({ value: item.id, label: labelOf(item, ["displayName", "username", "accountId"]) }))} />
          </Form.Item>
          <Form.Item name="facebookPageIds" label="Facebook Pages">
            <Select mode="multiple" options={facebookPages.map((item) => ({ value: item.id, label: labelOf(item, ["pageName", "pageId", "pageUrl"]) }))} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
